import {
  ActivationDocumentTypes,
  type AllDocumentTypes,
  DocumentTypes,
  documentTitles,
  HVACDocumentTypes,
  HVACInstallPackageFileCategory,
  InstallationPackagePhotoCategory,
  InstallDocumentTypes,
  InterconnectionAgreementDocumentType,
} from './DocumentTypes';
import { ProgramTypeLabels } from './ProgramTypes';

/**
 * A Document Pull checkbox category. Each category renders as one checkbox in the UI and
 * becomes one top-level folder in the export ZIP. A category may span multiple document
 * `types` — every document of those types lands in the category's folder (`slug`).
 *
 * This is the single source of truth shared by the frontend (checkbox rendering + expansion
 * to `selectedDocumentTypes`) and the backend (deriving the ZIP folder for each document).
 */
export interface DocumentPullCategory {
  /** Folder name inside the export ZIP, e.g. `install-photos`. */
  slug: string;
  /** Checkbox label shown in the UI, e.g. `Install Photos`. */
  label: string;
  /** Program-type labels that gate which categories are shown for an account. */
  programTypes: string[];
  /** Document types whose documents belong to this category. */
  documentTypes: AllDocumentTypes[];
}

const allProgramTypes = Object.values(ProgramTypeLabels);
const solarFamilyProgramTypes = [
  ProgramTypeLabels.solar,
  ProgramTypeLabels.doePr,
  ProgramTypeLabels.energyBackup,
  ProgramTypeLabels.newHomes,
  ProgramTypeLabels.newCommunity,
];

// Install Photos covers every install-photo category EXCEPT systemCommissioning, which has its
// own dedicated "System Commissioning" category. Keeping them disjoint guarantees every
// document type maps to exactly one category folder.
const installPhotoTypes = Object.values(InstallationPackagePhotoCategory).filter(
  (type) => type !== InstallationPackagePhotoCategory.systemCommissioning,
);

export const documentPullCategories: DocumentPullCategory[] = [
  {
    slug: 'contract',
    label: documentTitles.contract,
    programTypes: allProgramTypes,
    // Amendments modify the contract they amend, so they are always pulled alongside contracts rather
    // than being separately selectable.
    documentTypes: [DocumentTypes.contract, InstallDocumentTypes.installContract, DocumentTypes.amendment],
  },
  {
    slug: 'system-commissioning',
    label: documentTitles.systemCommissioning,
    programTypes: [ProgramTypeLabels.hvac],
    documentTypes: [InstallationPackagePhotoCategory.systemCommissioning],
  },
  {
    slug: 'electrician-sign-off',
    label: documentTitles.electricianSignOff,
    programTypes: [ProgramTypeLabels.doePr],
    documentTypes: [InstallDocumentTypes.electricianSignOff],
  },
  {
    slug: 'install-photos',
    label: 'Install Photos',
    programTypes: solarFamilyProgramTypes,
    documentTypes: installPhotoTypes,
  },
  {
    slug: 'interconnection-agreement',
    label: documentTitles.interconnectionAgreement,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [InterconnectionAgreementDocumentType.interconnectionAgreement],
  },
  {
    slug: 'permit',
    label: documentTitles.permit,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [InstallDocumentTypes.permit],
  },
  {
    slug: 'plan-set',
    label: documentTitles.planSet,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [InstallDocumentTypes.planSet],
  },
  {
    slug: 'production-model',
    label: documentTitles.productionModel,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [InstallDocumentTypes.productionModel],
  },
  {
    slug: 'proof-of-load',
    label: documentTitles.proofOfLoad,
    programTypes: [ProgramTypeLabels.doePr, ProgramTypeLabels.hvac],
    documentTypes: [InstallDocumentTypes.proofOfLoad, InstallDocumentTypes.proofOfLoadHVAC],
  },
  {
    slug: 'pto-confirmation',
    label: documentTitles.ptoConfirmation,
    programTypes: [
      ProgramTypeLabels.solar,
      ProgramTypeLabels.energyBackup,
      ProgramTypeLabels.newHomes,
      ProgramTypeLabels.newCommunity,
    ],
    documentTypes: [ActivationDocumentTypes.ptoConfirmation],
  },
  {
    slug: 'net-metering-approval',
    label: documentTitles.netMeteringApproval,
    programTypes: [
      ProgramTypeLabels.solar,
      ProgramTypeLabels.energyBackup,
      ProgramTypeLabels.newHomes,
      ProgramTypeLabels.newCommunity,
    ],
    documentTypes: [ActivationDocumentTypes.netMeteringApproval],
  },
  {
    slug: 'shade-report',
    label: documentTitles.shadeReport,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [InstallDocumentTypes.shadeReport],
  },
  {
    slug: 'utility-bill',
    label: documentTitles.utilityBill,
    programTypes: solarFamilyProgramTypes,
    documentTypes: [DocumentTypes.utilityBill],
  },
  {
    slug: 'completion-of-construction',
    label: documentTitles.completionOfConstruction,
    programTypes: allProgramTypes,
    documentTypes: [DocumentTypes.completionOfConstruction],
  },
  {
    slug: 'oem-warranty',
    label: 'OEM Warranty',
    programTypes: allProgramTypes,
    documentTypes: [
      // oemWarrantyDocuments shares the same string value across HVAC/WaterHeater/Generator/Geothermal enums
      HVACInstallPackageFileCategory.oemWarrantyDocuments,
      HVACDocumentTypes.oemWarranty,
    ],
  },
  {
    slug: 'extended-warranty',
    label: 'Extended Warranty',
    programTypes: allProgramTypes,
    documentTypes: [
      // extendedWarrantyDocuments shares the same string value across HVAC/WaterHeater/Generator/Geothermal enums
      HVACInstallPackageFileCategory.extendedWarrantyDocuments,
      ActivationDocumentTypes.extendedWarranty,
    ],
  },
];

/**
 * Reverse lookup from a document type to its category folder slug. Built from
 * `documentPullCategories`; throws at module load if any document type appears in more than one
 * category, so the disjointness invariant can't be silently broken by a future edit.
 */
export const documentTypeToCategorySlug: Record<string, string> = documentPullCategories.reduce(
  (map, category) => {
    for (const type of category.documentTypes) {
      if (map[type]) {
        throw new Error(
          `Document type "${type}" maps to multiple Document Pull categories ("${map[type]}" and "${category.slug}"). Categories must be disjoint.`,
        );
      }
      map[type] = category.slug;
    }
    return map;
  },
  {} as Record<string, string>,
);

/**
 * Returns the ZIP folder slug for a document type. Falls back to the raw type for any document
 * type not covered by a category (e.g. a direct API caller passing an arbitrary type) so files
 * are never dropped from the export.
 */
export function getCategorySlugForDocumentType(type: string): string {
  return documentTypeToCategorySlug[type] ?? type;
}
